import ToggleRow from '@components/Customization/ToggleRow';
import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import { useLocalization } from '@lib/localization';
import { openSsoWindow, SsoInsecureContextError, ssoRedirectUri, SsoWindowBlockedError } from '@lib/sso';
import { ContentCopy as CopyIcon, Key as KeyIcon } from '@mui/icons-material';
import { Alert, Box, Button, CardContent, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import SsoChangeDialog from '@components/Users/SsoChangeDialog';
import {
  type SsoConfig, type SsoConfigUpdate, type SsoImpact, type SsoTestResult, useFinishSsoTestLoginMutation, useGetSsoConfigQuery,
  usePreviewSsoImpactMutation, useStartSsoTestLoginMutation, useTestSsoConnectionMutation, useUpdateSsoConfigMutation,
} from '@store/api/ssoEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { useGetUsersQuery } from '@store/api/userEndpoints';
import { type ApiFieldError, fieldErrorsOf } from '@store/error';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { type FC, type ReactNode, useRef, useState } from 'react';
import { CardDescription, CardHeader, CardTitle } from './ServerConfig.styled';
import SsoDrawer from './SsoDrawer';
import { ssoErrorText, ssoFieldLabel } from './ssoFieldErrors';
import { ssoProviderPresets } from './ssoPresets';
import SsoTrialResult from './SsoTrialResult';

/** A secret this close to its end is worth a warning: replacing one takes somebody at the provider. */
const secretWarningDays = 30;

const Row: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} alignItems={{ sm: 'center' }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 190, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ minWidth: 0, flex: 1, fontSize: 14 }}>{children}</Box>
  </Stack>
);

/**
 * Signing in through an identity provider, for the sysadmin alone. The card shows the stored
 * settings and tries them; they are changed in the drawer its button opens. The two switches
 * save at once and by themselves, like the devices card's.
 *
 * Turning it on waits for a trial sign-in that passed with the connection as it is stored, so
 * that the first person to find a mistake is the one who can repair it: a provider set up
 * wrongly keeps out the accounts single sign-on manages, and turning it off again is what lets
 * them in. The sysadmin's password always works.
 *
 * Every save of the drawer and of the on/off switch goes through the server's dry run of the
 * same body first, and one that does something to accounts is shown as what it does to each of
 * them before it is made; the server, not the card, says which saves do. The password switch
 * places nobody and is not previewed. The two switches that decide who can get in at all, turning
 * it on or off and leaving passwords to the sysadmin, also say how many accounts that shuts out,
 * counted from the user list.
 */
const SsoCard: FC = () => {
  const { t, currentLanguage } = useLocalization();
  const dispatch = useAppDispatch();
  const { data: config, isFetching } = useGetSsoConfigQuery();
  const [updateSsoConfig, { isLoading: isSaving }] = useUpdateSsoConfigMutation();
  const [previewImpact, { isLoading: isPreviewing }] = usePreviewSsoImpactMutation();
  const [impact, setImpact] = useState<{ update: SsoConfigUpdate, notes: string[] } & SsoImpact>();
  // The lines the question before leaving passwords to the sysadmin says, while it is asked.
  const [passwordQuestion, setPasswordQuestion] = useState<string[]>();
  const { data: teams } = useGetTeamsQuery();
  const { data: users } = useGetUsersQuery();
  const [testConnection, { data: connection, isLoading: isTestingConnection, reset: resetConnection }] = useTestSsoConnectionMutation();
  const [startTestLogin] = useStartSsoTestLoginMutation();
  const [finishTestLogin] = useFinishSsoTestLoginMutation();

  const [isEditing, setEditing] = useState(false);
  // The fields the last save or preview was refused for: shown on them in the drawer, or under
  // the switches when the drawer is closed.
  const [fieldErrors, setFieldErrors] = useState<ApiFieldError[]>([]);
  const [isTrialWaiting, setTrialWaiting] = useState(false);
  const [trial, setTrial] = useState<SsoTestResult>();
  const trialAttempt = useRef(0);

  if (!config) return null;

  const isBusy = isSaving || isFetching || isPreviewing;
  const formatDate = (date: string) => new Date(date).toLocaleDateString(currentLanguage, { dateStyle: 'medium' });

  /** `refused`: the server named the fields at fault, which are now shown on them. */
  const save = async (update: SsoConfigUpdate): Promise<'saved' | 'refused' | 'failed'> => {
    const result = await updateSsoConfig(update);

    if (result.error) {
      setFieldErrors(fieldErrorsOf(result.error));

      return fieldErrorsOf(result.error).length > 0 ? 'refused' : 'failed';
    }

    // What was tried was the settings as they were.
    setTrial(undefined);
    resetConnection();
    setEditing(false);
    setFieldErrors([]);

    return 'saved';
  };

  const openDrawer = () => {
    setFieldErrors([]);
    setEditing(true);
  };

  const closeDrawer = () => {
    setEditing(false);
    setFieldErrors([]);
  };

  // The accounts that can sign in at all: the list leaves out the sysadmin, whose password always works.
  const active = (users ?? []).filter((user) => user.isActive !== false);
  // Without the list nothing can be counted, and the question is asked all the same.
  const isCounted = users !== undefined;

  /** What leaving passwords to the sysadmin shuts out. */
  const passwordNotes = (): string[] => {
    // An account single sign-on manages has no use of its password already.
    const withPassword = active.filter((user) => user.hasPassword && !user.ssoGoverned);
    const notLinked = withPassword.filter((user) => !user.ssoLinked).length;

    return [
      ...(withPassword.length > 0 ? [t('ServerConfig_Sso_Notes_PasswordsEnd', [withPassword.length])] : []),
      ...(notLinked > 0 ? [t('ServerConfig_Sso_Notes_NotLinked', [notLinked])] : []),
    ];
  };

  /** What turning single sign-on on or off shuts out, beyond what it does to the governed accounts. */
  const switchNotes = (update: SsoConfigUpdate): string[] => {
    if (update.enabled === false) {
      const withoutPassword = active.filter((user) => !user.hasPassword).length;

      return withoutPassword > 0 ? [t('ServerConfig_Sso_Notes_NoPassword', [withoutPassword])] : [];
    }

    // Turned on, a stored "passwords for the sysadmin only" applies again at once.
    return update.enabled === true && config.passwordLogin === 'sysadmin_only' ? passwordNotes() : [];
  };

  /** Whoever the provider lets through is given an admin account at their first sign-in. */
  const makesAdmins = (accounts: SsoConfig['accounts']) =>
    accounts.assignment === 'default' && accounts.autoCreate && accounts.defaultRole === 'admin';

  // Every save is asked about first, and the server says whether it touches any account: saved
  // at once where it does not, otherwise once the sysadmin has seen what it does.
  const saveChecked = async (update: SsoConfigUpdate) => {
    const result = await previewImpact(update);

    // Refused as the save would be: said on the fields at fault, and the drawer stays.
    if (result.error) {
      setFieldErrors(fieldErrorsOf(result.error));

      return;
    }

    setFieldErrors([]);

    const notes = [
      ...(result.data.unreachable ? [t('ServerConfig_Sso_Impact_Unreachable', [result.data.unreachable])] : []),
      ...switchNotes(update),
      // Asked once, when a save comes to it: no account changes, and nothing else would ask.
      ...(update.accounts && makesAdmins(update.accounts) && !makesAdmins(config.accounts) ? [t('ServerConfig_Sso_DefaultAdmin')] : []),
    ];

    if (result.data.changes.length === 0 && notes.length === 0 && (update.enabled === undefined || isCounted)) {
      await save(update);
    } else {
      setImpact({ update, ...result.data, notes });
    }
  };

  /**
   * The password switch, saved by itself: it places no account, so it is not previewed, and its
   * question counts from the user list. A refusal (a field the stored settings lack, say) is
   * shown under the switches like any other.
   */
  const savePasswordLogin = async (passwordLogin: SsoConfig['passwordLogin']): Promise<'saved' | 'refused' | 'failed'> => {
    const result = await updateSsoConfig({ passwordLogin });

    if (!result.error) {
      setFieldErrors([]);

      return 'saved';
    }

    setFieldErrors(fieldErrorsOf(result.error));

    return fieldErrorsOf(result.error).length > 0 ? 'refused' : 'failed';
  };

  const setPasswordLogin = (sysadminOnly: boolean) => {
    const notes = sysadminOnly ? passwordNotes() : [];

    // Letting passwords back in shuts nobody out.
    if (!sysadminOnly || (notes.length === 0 && isCounted)) {
      void savePasswordLogin(sysadminOnly ? 'sysadmin_only' : 'everyone');
    } else {
      setPasswordQuestion(notes);
    }
  };

  const copyRedirectUri = async () => {
    if (!config.redirectUri) return;

    await navigator.clipboard.writeText(config.redirectUri);
    dispatch(showSnackbar({ severity: 'info', message: t('Common_Copied') }));
  };

  const tryToSignIn = async () => {
    const attempt = ++trialAttempt.current;
    const isCurrent = () => attempt === trialAttempt.current;

    setTrial(undefined);
    setTrialWaiting(true);

    try {
      const outcome = await openSsoWindow(
        async (request) => (await startTestLogin({
          redirectUri: ssoRedirectUri(), codeChallenge: request.challenge, state: request.state
        }).unwrap()).url,
        { fallback: 'none', onWindowClosed: () => { if (isCurrent()) setTrialWaiting(false); } }
      );

      if (outcome.kind !== 'answer') return;

      // The server answers a trial with a code even when it failed, and the reason behind it.
      // No code means the trial itself was lost, most likely by taking too long.
      if (!outcome.answer.code) {
        if (isCurrent()) setTrial({ ok: false, error: outcome.answer.error ?? 'server_error' });

        return;
      }

      const result = await finishTestLogin({ code: outcome.answer.code, codeVerifier: outcome.request.verifier }).unwrap();

      if (isCurrent()) setTrial(result);
    } catch (error) {
      // Everything else was already said by the request that failed.
      if (error instanceof SsoWindowBlockedError) {
        dispatch(showSnackbar({ severity: 'warning', message: t('ServerConfig_Sso_Trial_WindowBlocked') }));
      } else if (error instanceof SsoInsecureContextError) {
        dispatch(showSnackbar({ severity: 'warning', message: t('SignIn_Sso_Error_InsecureContext') }));
      }
    } finally {
      if (isCurrent()) setTrialWaiting(false);
    }
  };

  const expiresAt = config.clientSecretExpiresAt ? new Date(config.clientSecretExpiresAt) : undefined;
  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86_400_000) : undefined;
  const hasConnection = Boolean(config.resolvedIssuer && config.clientId && config.clientSecret === 'set' && config.publicUrl);
  // A team deleted since the settings were saved stays named in them, and nobody is placed in it.
  const namesDeletedTeam = teams !== undefined && [
    ...config.accounts.defaultTeams.map(({ teamId }) => teamId),
    ...config.accounts.mappings.flatMap(({ teamId }) => (teamId ? [teamId] : [])),
  ].some((teamId) => !teams.some((team) => team._id === teamId));

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_Sso')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_Sso_Description')}</CardDescription>
        </Box>
        <Button variant="outlined" startIcon={<KeyIcon />} onClick={openDrawer} sx={{ flexShrink: 0, ml: 2 }}>
          {t('ServerConfig_Sso_Edit')}
        </Button>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        <Stack spacing={1.25}>
          <Row label={t('ServerConfig_Sso_Provider')}>
            {t(ssoProviderPresets[config.provider].label)}
            {config.resolvedIssuer && (
              <Typography component="div" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                {config.resolvedIssuer}
              </Typography>
            )}
          </Row>
          <Row label={t('ServerConfig_Sso_RedirectUri')}>
            {config.redirectUri
              ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
                    {config.redirectUri}
                  </Typography>
                  <IconButton size="small" aria-label={t('ServerConfig_Sso_Copy')} onClick={() => void copyRedirectUri()}>
                    <CopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              )
              : <Typography component="span" variant="body2" color="text.secondary">{t('ServerConfig_Sso_NotSet')}</Typography>}
          </Row>
          <Row label={t('ServerConfig_Sso_ClientSecret')}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <span>{t(`ServerConfig_Sso_Secret_${config.clientSecret.capitalize()}`)}</span>
              {expiresAt && daysLeft !== undefined && (
                <Chip
                  size="small"
                  color={daysLeft <= 0 ? 'error' : daysLeft <= secretWarningDays ? 'warning' : 'default'}
                  label={t(daysLeft <= 0 ? 'ServerConfig_Sso_Secret_Expired' : 'ServerConfig_Sso_Secret_ExpiresOn', [formatDate(config.clientSecretExpiresAt!)])}
                  sx={{ height: 22 }}
                />
              )}
            </Stack>
          </Row>
          <Row label={t('ServerConfig_Sso_Trial')}>
            {config.testedAt
              ? t('ServerConfig_Sso_Trial_PassedOn', [formatDate(config.testedAt)])
              : <Typography component="span" variant="body2" color="text.secondary">{t('ServerConfig_Sso_Trial_NotYet')}</Typography>}
          </Row>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          <Button
            variant="outlined" size="small" sx={buttonStyles}
            onClick={() => void testConnection()}
            disabled={!hasConnection || isTestingConnection || isBusy}
          >
            {t('ServerConfig_Sso_TestConnection')}
          </Button>
          <Button
            variant="outlined" size="small" sx={buttonStyles}
            onClick={() => void tryToSignIn()}
            disabled={!hasConnection || isTrialWaiting || isBusy}
          >
            {t(isTrialWaiting ? 'SignIn_Sso_Waiting' : 'ServerConfig_Sso_TrySignIn')}
          </Button>
        </Stack>

        {connection && (
          <Alert severity={connection.ok && connection.signingKeys > 0 && connection.pkce ? 'success' : 'error'} sx={{ mt: 2 }}>
            {connection.ok
              ? t('ServerConfig_Sso_Connection_Ok', [connection.issuer, String(connection.signingKeys)])
              : (
                <>
                  {t('ServerConfig_Sso_Connection_Failed')}
                  <Typography component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: 12.5, overflowWrap: 'anywhere' }}>
                    {connection.message}
                  </Typography>
                </>
              )}
            {connection.ok && !connection.pkce && <Box sx={{ mt: 0.5 }}>{t('ServerConfig_Sso_Connection_NoPkce')}</Box>}
            {connection.ok && connection.unsupportedScopes.length > 0 && (
              <Box sx={{ mt: 0.5 }}>{t('ServerConfig_Sso_Connection_UnsupportedScopes', [connection.unsupportedScopes.join(' ')])}</Box>
            )}
          </Alert>
        )}

        {trial && <SsoTrialResult result={trial} provider={config.provider} />}

        {namesDeletedTeam && <Alert severity="warning" sx={{ mt: 2 }}>{t('ServerConfig_Sso_DeletedTeams')}</Alert>}

        {/* On, with a connection changed since the trial that passed: the next sign-in finds out. */}
        {config.enabled && !config.testedAt && <Alert severity="warning" sx={{ mt: 2 }}>{t('ServerConfig_Sso_Untried')}</Alert>}

        <Stack spacing={1.5} sx={{ mt: 3 }}>
          <ToggleRow
            label={t('ServerConfig_Sso_Enabled')}
            description={t(config.enabled || config.testedAt ? 'ServerConfig_Sso_Enabled_Description' : 'ServerConfig_Sso_Enabled_NeedsTrial')}
            checked={config.enabled}
            onChange={(enabled) => void saveChecked({ enabled })}
            disabled={isBusy || (!config.enabled && !config.testedAt)}
          />
          <ToggleRow
            label={t('ServerConfig_Sso_PasswordSysadminOnly')}
            description={t('ServerConfig_Sso_PasswordSysadminOnly_Description')}
            checked={config.enabled && config.passwordLogin === 'sysadmin_only'}
            onChange={setPasswordLogin}
            disabled={isBusy || !config.enabled}
          />
        </Stack>

        {/* A switch refused, such as turning it on with a field still empty. */}
        {!isEditing && fieldErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('ServerConfig_Sso_Refused')}
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {fieldErrors.map((error) => (
                <li key={`${error.path}:${error.code ?? error.message}`}>
                  {ssoFieldLabel(t, error.path)}: {ssoErrorText(t, error)}
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </CardContent>

      <SsoDrawer
        open={isEditing}
        config={config}
        saving={isSaving || isPreviewing}
        errors={fieldErrors}
        onClose={closeDrawer}
        onSave={(update) => void saveChecked(update)}
      />
      <SsoChangeDialog
        open={impact !== undefined}
        title={t('ServerConfig_Sso_Impact_Title')}
        confirmLabel={t('Common_Save')}
        signOutLabel={(count) => t('ServerConfig_Sso_Impact_SaveAndSignOut', { count })}
        description={t('ServerConfig_Sso_Impact_Description')}
        changes={impact?.changes}
        unchanged={impact?.unchanged}
        notes={impact?.notes}
        onClose={() => setImpact(undefined)}
        // Refused for its fields, the dialog gives way to the drawer, which shows them.
        onConfirm={async () => (impact ? (await save(impact.update)) !== 'failed' : false)}
      />
      <ConfirmDialog
        open={passwordQuestion !== undefined}
        title={t('ServerConfig_Sso_PasswordOff_Title')}
        confirmLabel={t('Common_Save')}
        confirmColor="warning"
        onClose={() => setPasswordQuestion(undefined)}
        // Refused for a field, the question gives way to the alert under the switches.
        onConfirm={async () => (await savePasswordLogin('sysadmin_only')) !== 'failed'}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">{t('ServerConfig_Sso_PasswordSysadminOnly_Description')}</Typography>
          {passwordQuestion?.map((note) => <Alert key={note} severity="warning">{note}</Alert>)}
        </Stack>
      </ConfirmDialog>
    </Paper>
  );
};

export default SsoCard;
